import React from "react";
import { Table } from "antd";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { formatToCompanyTimezone } from "../../../lib/functions";

const ExpenseRecognitionTable = ({ printMode = false }) => {
  const { list, loading, filters } = useSelector(
    (state) => state.expenseRecognition
  );

  // Without details = header-level records
  const withoutDetailsColumns = [
    { title: "No.", render: (_, __, index) => index + 1, width: 60 },
    {
      title: "Date",
      dataIndex: "date",
      render: (v) =>
    v ? formatToCompanyTimezone(v).split(" ")[0] : "-",
    },
    { title: "Entry By", dataIndex: "createdBy" },
    { title: "Status", dataIndex: "status" },
    { title: "Description", dataIndex: "description" },
    { title: "Total Amount", dataIndex: "total", align: "right" },
  ];

  // Columns for child detail rows
  const detailColumns = [
    { title: "Sl No.", render: (_, __, index) => index + 1, width: 60 },
    { title: "Expense Head", dataIndex: "description" },
    { title: "Remark / Particulars", dataIndex: "remark" },
    { title: "Amount", dataIndex: "amount", align: "right" },
  ];

  // Columns for parent (when with details is chosen)
  const withDetailsColumns = [
    { title: "No.", render: (_, __, index) => index + 1, width: 60 },
    {
      title: "Date",
      dataIndex: "date",
      render: (val) => (val ? dayjs(val).format("DD-MM-YYYY") : "-"),
    },
    { title: "Description", dataIndex: "description" },
    { title: "Status", dataIndex: "status" },
    { title: "Entry By", dataIndex: "createdBy" },
    {
      title: "Total Amount",
      render: (_, record) => {
        const subtotal = record.details?.reduce(
          (sum, d) => sum + Number(d.amount || 0),
          0
        );
        return <strong>{subtotal?.toFixed(2)}</strong>;
      },
      align: "right",
    },
  ];

  const columns =
    filters.recordType === "with details"
      ? withDetailsColumns
      : withoutDetailsColumns;

  // Calculate grand total based on record type
  const grandTotal = list.reduce((sum, row) => {
    if (filters.recordType === "with details") {
      const subtotal = row.details?.reduce(
        (s, d) => s + Number(d.amount || 0),
        0
      );
      return sum + subtotal;
    }
    return sum + Number(row.total || 0);
  }, 0);

  if (printMode && filters.recordType !== "with details") {
  return (
    <table className="ledger-print-table">
      <colgroup>
        <col style={{ width: "5%" }} />
        <col style={{ width: "12%" }} />
        <col style={{ width: "15%" }} />
        <col style={{ width: "10%" }} />
        <col style={{ width: "38%" }} />
        <col style={{ width: "20%" }} />
      </colgroup>

      <tbody>
        <tr className="ledger-print-heading">
          <td>No.</td>
          <td>Date</td>
          <td>Entry By</td>
          <td>Status</td>
          <td>Description</td>
          <td style={{ textAlign: "right" }}>Total Amount</td>
        </tr>

        {list.map((row, index) => (
          <tr key={row.id || index}>
            <td>{index + 1}</td>
            <td>
              {row.date
                ? formatToCompanyTimezone(row.date).split(" ")[0]
                : "-"}
            </td>
            <td>{row.createdBy}</td>
            <td>{row.status}</td>
            <td
              style={{
                wordBreak: "break-word",
                overflowWrap: "break-word",
              }}
            >
              {row.description}
            </td>
            <td style={{ textAlign: "right" }}>
              {Number(row.total || 0).toFixed(2)}
            </td>
          </tr>
        ))}

        <tr className="ledger-total-row">
          <td colSpan={5} style={{ textAlign: "right" }}>
            Grand Total
          </td>
          <td style={{ textAlign: "right" }}>
            {grandTotal.toFixed(2)}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

  return (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={list}
      loading={loading}
      pagination={printMode ? false : { pageSize: 10 }}
      bordered
      size={printMode ? "small" : "middle"}
      expandable={
        filters.recordType === "with details"
          ? {
              expandedRowRender: (record) => {
                const subtotal = record.details?.reduce(
                  (sum, d) => sum + Number(d.amount || 0),
                  0
                );
                return (
                  <>
                    <Table
                      rowKey="detailId"
                      columns={detailColumns}
                      dataSource={record.details || []}
                      pagination={false}
                      size="small"
                      bordered
                      summary={() => (
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={3}>
                            <strong>Sub Total</strong>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={2} align="right">
                            <strong>{subtotal?.toFixed(2)}</strong>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      )}
                    />
                  </>
                );
              },
              rowExpandable: (record) => record.details?.length > 0,
            }
          : undefined
      }
      summary={() => {
        const visibleCols =
          columns.length + (filters.recordType === "with details" ? 1 : 0);
        if (filters.recordType === "with item") {
          const grand = list.reduce(
            (acc, row) => {
              const sub = row.details?.reduce(
                (s, d) => {
                  s.masterQty += Number(d.item_qty || 0);
                  s.baseQty += Number(d.retail_qty || 0);
                  s.total += Number(d.item_total || 0);
                  return s;
                },
                { masterQty: 0, baseQty: 0, total: 0 }
              );
              acc.masterQty += sub.masterQty;
              acc.baseQty += sub.baseQty;
              acc.total += sub.total;
              return acc;
            },
            { masterQty: 0, baseQty: 0, total: 0 }
          );

          return (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={visibleCols - 1}>
                <strong>
                  Grand Total: Master U. Qty = {grand.masterQty}, Base U. Qty ={" "}
                  {grand.baseQty}
                </strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={visibleCols - 1} align="right">
                <strong>{grand.total.toFixed(2)}</strong>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          );
        } else {
          return (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={visibleCols - 1}>
                <strong>Grand Total</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={visibleCols - 1} align="right">
                <strong>{grandTotal.toFixed(2)}</strong>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          );
        }
      }}
    />
  );
};

export default ExpenseRecognitionTable;
